/**
 * The item grid — the tiles a cashier taps to build an order.
 *
 * Design rules:
 *   Tiles are tall enough to read and tap at speed across a counter.
 *   Sold-out tiles stay visible (greyed) so the cashier can explain quickly.
 *   Items missing fiscal data are flagged before the gateway rejects them.
 *
 * The column count is chosen against the width the grid actually gets, not the
 * width of the screen: from `lg` the order panel takes a third of it back, so
 * the count drops there rather than rising. Two columns is the floor - one
 * column of full-width tiles reads as a list, and the list view already exists
 * for people who want that.
 */

import { useSelector } from 'react-redux';
import { FiAlertTriangle, FiPackage, FiSearch, FiPlus } from 'react-icons/fi';

import { selectVisibleItems, selectMenu } from '../../store/menuSlice';
import { money } from '../../utils/format';
import { assetUrl } from '../../services/api';
import { EmptyState } from '../ui/Feedback';

/* ─────────────────────────────── grid tile ─────────────────────────────── */

const ItemTile = ({ item, onSelect }) => {
    const disabled = !item.isAvailable;

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => onSelect(item)}
            aria-label={`${item.name}, Rs. ${money(item.price)}${disabled ? ', sold out' : ''}`}
            className={`group relative flex flex-col overflow-hidden rounded-2xl border-0 bg-white text-left
                        shadow-tile transition-all duration-200
                        ${disabled
                    ? 'cursor-not-allowed opacity-50 grayscale'
                    : 'hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(15,23,42,0.12)] active:translate-y-0 active:shadow-tile'}`}
        >
            {/* Image / colour swatch */}
            <div
                className="relative h-[4.5rem] w-full overflow-hidden sm:h-[5.5rem]"
                style={!item.imagePath && item.colour ? { backgroundColor: `${item.colour}18` } : { backgroundColor: '#f8fafc' }}
            >
                {item.imagePath ? (
                    <img
                        src={assetUrl(item.imagePath)}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    /* Monogram fallback */
                    <div className="flex h-full w-full items-center justify-center">
                        <span
                            className="text-3xl font-black tracking-tight opacity-20 select-none"
                            style={{ color: item.colour || '#64748b' }}
                        >
                            {item.name.slice(0, 2).toUpperCase()}
                        </span>
                    </div>
                )}

                {/* Gradient overlay on images for legibility */}
                {item.imagePath && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
                )}

                {/* Colour accent line at top */}
                {item.colour && (
                    <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: item.colour }} />
                )}

                {/* Deal badge */}
                {item.isDeal && (
                    <span className="absolute left-2 top-2 rounded-full bg-amber-500 px-2 py-0.5
                                     text-[9px] font-black uppercase tracking-wider text-white shadow-sm">
                        Deal
                    </span>
                )}

                {/* Variant count */}
                {item.variants?.length > 0 && (
                    <span className="absolute right-2 top-2 rounded-full bg-slate-900/65
                                     px-2 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
                        {item.variants.length} sizes
                    </span>
                )}

                {/* Fiscal warning dot */}
                {!item.fiscalReady && (
                    <span
                        className="absolute bottom-1.5 right-1.5 flex h-5 w-5 items-center justify-center
                                   rounded-full bg-red-500 shadow-sm text-white"
                        title="Missing HS code or tax rate — FBR will reject this item"
                    >
                        <FiAlertTriangle className="h-3 w-3" />
                    </span>
                )}

                {/* Sold-out overlay */}
                {disabled && (
                    <span className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                        <span className="rounded-full bg-slate-700 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
                            Sold Out
                        </span>
                    </span>
                )}
            </div>

            {/* Info row */}
            <div className="flex flex-1 flex-col justify-between p-2.5 pt-2">
                <p className="line-clamp-2 text-[0.78rem] font-semibold leading-snug text-slate-800">
                    {item.name}
                </p>
                <div className="mt-1.5 flex items-center justify-between">
                    <p className="tabular text-xs font-bold text-brand-700">
                        Rs. {money(item.price)}
                        {item.variants?.length > 0 && (
                            <span className="ml-0.5 text-[10px] font-normal text-slate-400">+</span>
                        )}
                    </p>
                    {/* Add hint — visible on hover */}
                    {!disabled && (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full
                                         bg-brand-700 text-white opacity-100 transition-opacity
                                         [@media(hover:hover)]:opacity-0 group-hover:opacity-100">
                            <FiPlus className="h-3 w-3" />
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
};

/* ─────────────────────────────── list row ──────────────────────────────── */

const ItemRow = ({ item, onSelect }) => {
    const disabled = !item.isAvailable;

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => onSelect(item)}
            className={`group flex w-full items-center gap-3 rounded-xl bg-white px-3.5 py-3 text-left
                        shadow-tile transition-all duration-150
                        ${disabled
                    ? 'cursor-not-allowed opacity-50 grayscale'
                    : 'hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(15,23,42,0.10)]'}`}
        >
            {/* Avatar */}
            <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl
                           overflow-hidden text-xs font-bold"
                style={item.colour
                    ? { backgroundColor: `${item.colour}18`, color: item.colour }
                    : { backgroundColor: '#f1f5f9', color: '#64748b' }}
            >
                {item.imagePath
                    ? <img src={assetUrl(item.imagePath)} alt="" className="h-full w-full object-cover" />
                    : <span className="text-sm font-black">{item.name.slice(0, 2).toUpperCase()}</span>
                }
            </div>

            {/* Text */}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">{item.name}</p>
                <p className="truncate text-xs text-slate-400">
                    {item.categoryName}
                    {item.code && <span className="ml-1 font-mono">· {item.code}</span>}
                    {disabled && <span className="text-red-400"> · sold out</span>}
                </p>
            </div>

            {/* Fiscal flag */}
            {!item.fiscalReady && (
                <FiAlertTriangle className="h-4 w-4 shrink-0 text-red-500" title="Missing HS code or tax rate" />
            )}

            {/* Price */}
            <p className="tabular shrink-0 text-sm font-bold text-brand-700">
                Rs. {money(item.price)}
            </p>

            {/* Add arrow */}
            {!disabled && (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full
                                 bg-brand-700 text-white opacity-100 transition-opacity
                                 [@media(hover:hover)]:opacity-0 group-hover:opacity-100">
                    <FiPlus className="h-3.5 w-3.5" />
                </span>
            )}
        </button>
    );
};

/* ─────────────────────────────── the grid ──────────────────────────────── */

const ItemGrid = ({ onSelect }) => {
    const items = useSelector(selectVisibleItems);
    const { viewMode, search, status } = useSelector(selectMenu);

    if (status === 'loading') {
        return (
            <div className="grid grid-cols-2 gap-2 xs:grid-cols-3 sm:grid-cols-4 sm:gap-2.5
                            lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="h-[8rem] animate-pulse rounded-2xl bg-white/80 sm:h-[9rem]" />
                ))}
            </div>
        );
    }

    if (!items.length) {
        return (
            <EmptyState
                icon={search ? FiSearch : FiPackage}
                title={search ? `Nothing matches "${search}"` : 'No items in this category'}
                description={
                    search
                        ? 'Try a shorter search, or scan the barcode instead.'
                        : 'Add items to this category from Menu Items.'
                }
            />
        );
    }

    if (viewMode === 'list') {
        return (
            <div className="space-y-1.5">
                {items.map((item) => (
                    <ItemRow key={item.menuItemId} item={item} onSelect={onSelect} />
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 gap-2 xs:grid-cols-3 sm:grid-cols-4 sm:gap-2.5
                            lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
            {items.map((item) => (
                <ItemTile key={item.menuItemId} item={item} onSelect={onSelect} />
            ))}
        </div>
    );
};

export { ItemTile, ItemRow };
export default ItemGrid;
